export default {
    accountTypes: {
        bank: 'Bank account',
        creditCard: 'Credit card',
        cash: 'Cash',
        savings: 'Savings',
    },
    intervals: {
        monthly: 'monthly',
        quarterly: 'quarterly',
        yearly: 'yearly',
    },
    days: {
        today: 'today',
        yesterday: 'yesterday',
        todayHeading: 'Today',
        yesterdayHeading: 'Yesterday',
        untilMonth: 'by {month} {year}',
    },
    due: {
        paid: 'paid',
        skipped: 'skipped',
        overdue: '{n} day overdue | {n} days overdue',
        today: 'today',
        tomorrow: 'tomorrow',
        inDays: 'in {n} days',
    },
    firstSteps: {
        account: {
            title: 'A second account', action: 'Add account',
            what: 'Cash, a credit card or a savings account — each has its own balance.',
            why: 'Spending cash doesn’t change your bank balance. With separate accounts the totals add up, and a transfer between them doesn’t count as an expense.',
        },
        expense: {
            title: 'Your first expense', action: 'Add expense',
            what: 'Amount and category — account, date and description are suggested, no need to touch them.',
            why: 'Every expense immediately lowers “Left to spend”, so you see how much is really left until the end of the month.',
        },
        receipt: {
            title: 'Expense from a receipt', action: 'Photograph a receipt',
            what: 'A photo of a receipt or a PDF — we’ll read the shop, date, amount and category.',
            why: 'Fastest right after shopping: check the suggestion, fix what’s needed and add it with one tap. The photo stays with the transaction.',
        },
        dictation: {
            title: 'Dictate transactions', action: 'Dictate',
            what: 'Say or type a sentence, e.g. “yesterday Tesco 84, today fuel 50”.',
            why: 'Several transactions at once when you’re catching up — you’ll see and correct each one before saving.',
        },
        income: {
            title: 'Your first income', action: 'Add income',
            what: 'Salary, a refund, a sale — anything that comes into your account.',
            why: 'Income increases “Left to spend”. Also add a monthly salary as recurring income in Plans — it’ll be counted before it arrives.',
        },
        transfer: {
            title: 'Transfer between accounts', action: 'Add transfer',
            what: 'E.g. a cash machine withdrawal or paying off a credit card from your current account.',
            why: 'It’s not an expense — the money just changes accounts. It doesn’t inflate your expenses or mess up balances.',
        },
        plan: {
            title: 'Recurring payment', action: 'Add recurring payment',
            what: 'Rent, electricity, a subscription — the amount and the day of the month.',
            why: 'It’s deducted from “Left to spend” before you pay it, so the rent money doesn’t disappear. On the due date you tick it off with one tap.',
        },
        budget: {
            title: 'Category budget', action: 'Set budget',
            what: 'A monthly limit for a category, e.g. 300 for food.',
            why: 'At 80% of the limit you’ll see a warning on the main page — before it’s too late.',
        },
        goal: {
            title: 'Savings goal', action: 'Create goal',
            what: 'What you’re saving for and how much — by a deadline or a fixed amount each month.',
            why: 'Wallet works out how much to set aside this month and deducts it from “Left to spend”. You see your progress and when you’ll reach the goal.',
        },
        loan: {
            title: 'Loan', action: 'Add loan',
            what: 'An instalment loan or a revolving one (card, overdraft).',
            why: 'Instalments appear in “Upcoming” like recurring payments, and overpayments shorten the repayment — you can see how much is left.',
        },
        statement: {
            title: 'Statement import', action: 'Load statement',
            what: 'A CSV file with your account history, downloaded from online banking.',
            why: 'Fill in what’s missing: we’ll recognise what’s already there, what pays a recurring payment and what’s an own transfer. Nothing is saved without your approval.',
        },
        reminders: {
            title: 'Reminders', action: 'Turn on reminders',
            what: 'A notification on this device before a recurring payment is due and on the day.',
            why: 'No payment will go overdue, even when you don’t open Wallet.',
        },
    },
    help: {
        leftToSpend: {
            title: 'Left to spend',
            text: 'The most important number: income minus expenses minus this month’s recurring payments not yet paid. Below it — how much that is per day.',
            more: 'Amounts you’re setting aside for goals this month are deducted too. Movements on savings accounts don’t count — that’s not money to spend.',
        },
        quickAdd: {
            title: 'An expense in two seconds',
            text: 'An expense, income or a transfer between accounts. In the form you can also dictate several transactions at once.',
            more: 'An own transfer (e.g. a cash machine withdrawal, paying off a credit card) isn’t an expense — the money just changes accounts.',
        },
        receipt: {
            title: 'Receipt photo',
            text: 'Take a photo of a receipt or pick a PDF — we’ll read the shop, date, amount and category. Check, fix what’s needed and add it with one tap. The photo stays with the transaction.',
        },
        upcoming: {
            title: 'Upcoming payments',
            text: 'Recurring payments and instalments for the next month. The circle marks it paid with one tap, “Skip” — not needed this month.',
            more: 'A paid payment becomes an expense on its account (or the default one). Until you tick it off, it’s still deducted from “Left to spend”.',
        },
        accounts: {
            title: 'Accounts',
            text: 'The balance of each account and the total. Tap an account to see its transactions.',
        },
        budgets: {
            title: 'Budgets',
            text: 'When a category reaches 80% of its limit, you’ll see it here — before it’s too late.',
            more: 'You set limits in Analysis: click a category and enter a monthly amount.',
        },
        setAside: {
            title: 'Set aside this month',
            text: 'How much to set aside for each goal this month. “Set aside” records an own transfer to the goal’s account.',
        },
        txFilters: {
            title: 'Transactions',
            text: 'The whole month day by day. Search by payee, filter by account and category; the arrows in the header change the month.',
            more: 'The total below the filters only counts what’s visible. Tap a transaction to edit it, split it into categories or add a receipt photo.',
        },
        txImport: {
            title: 'Statement import',
            text: 'Load a file with your account history from the bank — we’ll recognise what’s already there, what pays a plan and what’s an own transfer. Nothing is saved without your approval.',
            more: 'You can load the same file again — rows that are already there will be skipped.',
        },
        plansRecurring: {
            title: 'Recurring payments and income',
            text: 'Rent, subscriptions, salary — entered once, they’re deducted from “Left to spend” every month before you pay or receive them.',
            more: 'They can be monthly, quarterly or yearly. A reminder will arrive on the due date.',
        },
        plansIncome: {
            title: 'Recurring income',
            text: 'Salary, a benefit, rent from a tenant — income that comes in regularly. Before you receive it, “Left to spend” already includes it as “Still to come”.',
        },
        plansGoals: {
            title: 'Savings goals',
            text: 'Save for something by a deadline or a fixed amount each month — you’ll see your progress and how much is left to set aside.',
            more: 'The goal’s money sits in the chosen account (ideally a savings one). A goal can deduct its monthly amount from “Left to spend” so it doesn’t get spent.',
        },
        plansLoans: {
            title: 'Loans',
            text: 'Tick off instalments like recurring payments; overpayments shorten the repayment.',
            more: 'For a revolving loan (card, overdraft) the instalment is the amount you plan to repay each month.',
        },
        analysisResult: {
            title: 'Month result',
            text: 'Income, expenses and the balance of the financial month. “You’re saving” is the part of your income that hasn’t been spent.',
        },
        analysisCategories: {
            title: 'Where the money goes',
            text: 'Expenses by category with budgets. This is where you set a monthly limit for a category.',
            more: 'A budget can apply every month or just this one. The line on the bar is the limit.',
        },
        analysisIncome: {
            title: 'Where it comes from',
            text: 'This month’s income by category. Tap a category to see its transactions.',
        },
        analysisTrend: {
            title: 'Month to month',
            text: 'Expenses from recent months and the average. Tap a bar to go to that month.',
        },
        settingsAccounts: {
            title: 'Accounts and categories',
            text: 'Add accounts (bank, cash, credit card, savings) and arrange your own categories.',
            more: '“Reconcile” enters the balance from your bank — the difference is saved as an adjustment. “Statement” loads the account history.',
        },
        settingsCategories: {
            title: 'Categories',
            text: 'Your own expense and income categories. Deleting a category moves its transactions to another one you choose — nothing is left uncategorised.',
        },
        settingsReminders: {
            title: 'Reminders',
            text: 'A notification on the day of a recurring payment — none will go overdue.',
            more: 'Reminders are turned on separately on each device.',
        },
        settingsMonth: {
            title: 'Financial month',
            text: 'The day your month starts. If your salary arrives on the 10th, set 10 — “Left to spend” and budgets then run from payday to payday.',
        },
    },
    tour: {
        welcome: {
            title: 'Welcome to Wallet',
            text: 'How much you can still spend, what needs paying and where your money goes. In two minutes I’ll show you where everything is — you can close this at any time.',
        },
        done: {
            title: 'That’s all',
            text: 'The guide is always available under the “Help” button — Getting started is there too. And “What’s this?” next to each section explains it without the guide.',
        },
    },
    now: {
        title: 'Now',
        add: 'Add',
        addTransaction: 'Add transaction',
        leftToSpendUntil: 'LEFT TO SPEND · UNTIL {date}',
        daily: 'about {amount} a day for {n} day | about {amount} a day for {n} days',
        overspent: 'Expenses and planned payments exceed this month’s income.',
        startingBalance: 'Starting balance',
        startingBalanceHint: 'First month in Wallet: what was in your accounts and cash at the start counts',
        income: 'Income',
        pendingIncome: 'Still to come',
        spent: 'Spent',
        toPay: 'To pay',
        savings: 'Savings and goals',
        savingsHint: 'Transfers to savings and what you still need to set aside for goals this month (movements on savings accounts aren’t money to spend)',
        expense: 'Expense',
        incomeOne: 'Income',
        transfer: 'Own transfer',
        receipt: 'Receipt',
        receiptHint: 'A receipt photo or PDF — we’ll read the amount and category',
        accounts: 'ACCOUNTS',
        manage: 'Manage',
        upcoming: 'UPCOMING',
        overdueCount: '{n} overdue',
        plans: 'Plans',
        markReceived: '{name}: mark as received',
        markPaid: '{name}: mark as paid',
        received: 'Received',
        paid: 'Paid',
        loanInstalment: 'loan instalment',
        skip: 'Skip',
        skipHint: 'No need to pay this month',
        allSettled: 'Everything for the next month is settled.',
        noPlans: 'No recurring payments. {link} — they’ll be deducted from “Left to spend” before you pay them.',
        noPlansLink: 'Add rent, subscriptions, salary',
        settledCount: '{n} already settled',
        setAsideTitle: 'SET ASIDE THIS MONTH',
        goals: 'Goals',
        ofTotal: '{part} of {total}',
        setAsideAmount: 'Set aside {amount}',
        setAsideDone: 'set aside',
        budgets: 'BUDGETS',
        analysis: 'Analysis',
        overBy: 'over by {amount}',
        left: '{amount} left',
        budgetsOk: 'All budgets on track.',
        noBudgets: 'Set category limits in {link}, and you’ll see a warning here at 80%.',
        noBudgetsLink: 'Analysis',
        recent: 'RECENT',
        all: 'All',
        receiptPhotos: 'Receipt photos: {n}',
        hasReceiptPhoto: 'has a receipt photo',
        noTransactions: 'Nothing added yet. Start with your first expense — the {button} button at the top.',
        flash: {
            received: '“{name}” received.',
            paid: '“{name}” paid.',
            saveFailed: 'Couldn’t save.',
            setAside: 'Set aside for “{name}”.',
            alreadyOnGoalAccount: 'Your default account is the goal’s account — transfer from another account.',
            setAsideFailed: 'Couldn’t set aside.',
            skipped: '“{name}” skipped this month.',
            skipFailed: 'Couldn’t skip.',
            photoFailed: 'Saved, but the photos couldn’t be uploaded — add them when editing.',
            transferSaved: 'Transfer saved.',
            added: 'Added. Left to spend: {amount}',
            saved: 'Saved.',
            deleted: 'Deleted.',
            addedCount: 'Added: {n} transaction. | Added: {n} transactions.',
        },
    },
    transactions: {
        title: 'Transactions',
        add: 'Add',
        loadFailed: 'Couldn’t load transactions.',
        monthLoadFailed: 'Couldn’t load the month.',
        archivedAccount: '{name} (archived)',
        expenses: 'Expenses',
        income: 'Income',
        instalmentOf: 'instalment: {name}',
        overpaymentOf: 'overpayment: {name}',
        importHint: 'Load your account history from the bank (CSV)',
        import: 'Statement import',
        prevMonth: 'Previous month',
        nextMonth: 'Next month',
        search: 'Search…',
        searchLabel: 'Search by payee or note',
        allAccounts: 'All accounts',
        allCategories: 'All categories',
        allTypes: 'Expenses and income',
        count: '{n} transaction | {n} transactions',
        incomeLabel: 'income',
        expensesLabel: 'expenses',
        ownTransfer: 'own transfer',
        noMatches: 'Nothing matches the filters.',
        empty: 'No transactions this month.',
        flash: {
            photoFailed: 'Saved, but the photos couldn’t be uploaded — add them when editing.',
            added: 'Added.',
            saved: 'Saved.',
            deleted: 'Deleted.',
            imported: 'Statement imported.',
        },
    },
    analysis: {
        title: 'Analysis',
        income: 'Income',
        expenses: 'Expenses',
        balance: 'Balance',
        saving: 'You’re saving',
        noIncomeShort: 'no income',
        whereMoneyGoes: 'WHERE THE MONEY GOES',
        clickToBudget: 'click a category to set a budget',
        ofBudget: 'of {amount}',
        overBudget: 'Budget exceeded by {amount}',
        left: '{amount} left',
        noExpenses: 'No expenses this month.',
        otherCategory: 'Set a budget for another category',
        whereFrom: 'WHERE IT COMES FROM',
        noIncome: 'No income this month.',
        trendTitle: 'EXPENSES · {n} MONTHS',
        average: 'average {amount} / month',
        budget: {
            limit: 'Monthly limit',
            limitHint: 'You’ll see a warning at 80%.',
            scope: 'Scope',
            onlyMonth: 'Only {month}',
            saved: 'Budget “{name}” saved.',
            saveFailed: 'Couldn’t save the budget.',
            deleted: 'Budget “{name}” deleted.',
            deleteFailed: 'Couldn’t delete the budget.',
            dialogTitle: 'Budget: {name}',
            deleteSubject: 'This category’s budget',
        },
        budgetStep: {
            title: 'Set budget',
            text: 'Click a category (or “Set a budget for another category” below the list) and enter a monthly limit. At 80% you’ll see a warning on the main page.',
        },
    },
    plans: {
        title: 'Plans',
        action: 'Recurring payment',
        add: '+ Add',
        payments: 'RECURRING PAYMENTS',
        paymentsEmpty: 'Rent, utilities, subscriptions, insurance — tick each one off with a single tap.',
        incomes: 'RECURRING INCOME',
        incomesEmpty: 'Salary or other regular income — “Left to spend” will count it before it arrives.',
        instalmentsLeft: '{n} instalment left | {n} instalments left',
        schedule: '{interval}, day {day}',
        until: 'until {month}',
        goals: 'GOALS',
        createSavings: 'Create Savings',
        addGoal: '+ Add goal',
        perMonth: '{amount} / month',
        goalAchieved: 'Reached — you can spend it and close the goal.',
        thisMonth: 'This month {part} of {total}',
        atThisPace: 'at this pace: {month}',
        setAsideThisMonth: 'set aside this month',
        notReserved: 'not deducted from “Left to spend”',
        goalsEmpty: 'A holiday, an emergency fund, a new phone — Wallet works out how much to set aside each month and deducts it from “Left to spend” before the money gets spent.',
        finished: 'Finished:',
        loans: 'LOANS',
        addLoan: '+ Add loan',
        loansMonthly: 'instalments {amount} / month',
        revolvingRate: 'revolving · {rate}%',
        installmentLoan: 'instalment loan',
        ends: 'ends {date}',
        instalment: 'instalment',
        principal: 'Principal',
        interestLeft: 'Interest to the end',
        payInstalment: 'Pay instalment · {due}',
        instalmentPaid: 'this month’s instalment paid',
        payNext: 'Pay next instalment',
        loansEmpty: 'Loan instalments work like recurring payments, and Wallet also tracks principal, interest and the effect of overpayments. A credit card is an account — add it in settings.',
        form: {
            expenses: 'Expenses',
            incomes: 'Income',
            name: 'Name',
            namePlaceholder: 'e.g. Rent',
            namePlaceholderIncome: 'e.g. Salary',
            kind: 'Type',
            kindPayment: 'Payment',
            kindIncome: 'Income',
            amount: 'Amount',
            category: 'Category',
            interval: 'How often',
            dueDay: 'Day of the month',
            startMonth: 'From month',
            startMonthHint: 'Quarterly and yearly ones count from this month.',
            endMonth: 'Until month',
            endMonthHint: 'Empty = no end.',
            fromAccount: 'From account',
            defaultAccount: 'Default account',
        },
        loan: {
            namePlaceholder: 'e.g. Mortgage',
            installment: 'Instalment',
            revolving: 'Revolving / overdraft',
            installmentAmount: 'Instalment / monthly repayment',
            capital: 'Principal left',
            capitalHint: 'From your banking app — we calculate interest from it.',
            interestRate: 'Interest rate',
            paymentDay: 'Instalment day',
            nextPayment: 'Next instalment',
            nextPaymentHint: 'Empty = the next instalment day.',
            lastPayment: 'Last instalment',
            installmentType: 'Instalments',
            equal: 'Equal',
            decreasing: 'Decreasing',
            minimumPayment: 'Minimum payment',
            category: 'Instalment category',
        },
        overpay: {
            button: 'Overpay',
            amount: 'Overpayment amount',
            date: 'Date',
            mode: 'What the bank does',
            shorten: 'Shortens the term (same instalment)',
            lower: 'Lowers the instalment (same end date)',
            capitalOnly: 'Only reduces the principal',
            account: 'Loan account or default',
        },
        goal: {
            name: 'What for',
            namePlaceholder: 'e.g. Holiday',
            target: 'How much to save',
            pace: 'How',
            byDeadline: 'By a deadline',
            fixedMonthly: 'Fixed amount each month',
            monthly: 'Each month',
            initial: 'Already saved',
            account: 'In account',
            noSavingsAccount: 'You don’t have a savings account — “Create Savings” below.',
            reserve: 'Left to spend',
            reserveHint: 'Deduct this month’s contribution before I make it',
            archived: 'Finished',
            archivedHint: 'Goal reached or abandoned — it stays in the history',
        },
        dialog: {
            editPlan: 'Edit plan',
            newIncome: 'New recurring income',
            newPayment: 'New recurring payment',
            editLoan: 'Edit loan',
            newLoan: 'New loan',
            editGoal: 'Edit goal',
            newGoal: 'New goal',
            overpay: 'Overpayment: {name}',
        },
        flash: {
            alreadySettled: 'Already settled this month.',
            saveFailed: 'Couldn’t save.',
            settled: '“{name}” settled.',
            instalmentPaid: '“{name}” instalment paid.',
            planSaved: 'Plan saved.',
            planSaveFailed: 'Couldn’t save the plan.',
            planDeleted: 'Plan deleted. Its transactions remain.',
            planDeleteFailed: 'Couldn’t delete the plan.',
            loanSaved: 'Loan saved.',
            loanSaveFailed: 'Couldn’t save the loan.',
            loanDeleted: 'Loan deleted. Instalment transactions remain.',
            loanDeleteFailed: 'Couldn’t delete the loan.',
            overpaySaved: 'Overpayment saved — loan recalculated.',
            overpaySaveFailed: 'Couldn’t save the overpayment.',
            goalSaved: 'Goal saved.',
            goalSaveFailed: 'Couldn’t save the goal.',
            goalDeleted: 'Goal deleted. The transfers remain.',
            goalDeleteFailed: 'Couldn’t delete the goal.',
            savingsCreated: 'Added the “{name}” account. You can reconcile its balance in settings.',
            accountAddFailed: 'Couldn’t add the account.',
        },
    },
    settings: {
        title: 'Wallet settings',
        save: 'Save',
        addAccount: '+ Add account',
        default: 'default',
        archive: 'archived',
        limit: 'limit {amount}',
        statement: 'Statement',
        reconcileButton: 'Reconcile',
        reconcileHint: 'Enter the balance from your bank',
        creditCardNote: 'A credit card is an account: card purchases are expenses, and paying off the card from your current account is an own transfer.',
        expenseCategories: 'EXPENSE CATEGORIES',
        incomeCategories: 'INCOME CATEGORIES',
        untrackedHint: 'Used when reconciling the balance',
        account: {
            name: 'Name',
            namePlaceholder: 'e.g. Current account',
            type: 'Type',
            openingBalance: 'Opening balance',
            openingBalanceHint: 'For a credit card, enter the debt with a minus.',
            creditLimit: 'Card limit',
            number: 'Account number',
            numberPlaceholder: 'e.g. last 4 digits',
            numberHint: 'Helps recognise own transfers in statements.',
            isDefault: 'Default',
            isDefaultHint: 'Default when adding',
            archived: 'Archive',
            archivedHint: 'Hide (history stays)',
        },
        reconcile: {
            balance: 'How much is in the account now',
            hint: 'The difference will be saved as a transaction in the “Untracked” category.',
            matches: 'The balance matches.',
            done: 'Balance reconciled ({amount} as untracked).',
        },
        category: {
            expenses: 'Expenses',
            income: 'Income',
            moveTo: 'Move transactions, plans and budgets to',
        },
        history: {
            title: 'HISTORY FROM FINANCES',
            text: 'In Finances: {list}. You can copy them once — Finances will stay unchanged. After that this option disappears.',
            expenses: '{n} expense | {n} expenses',
            incomes: '{n} income entry | {n} income entries',
            fixedPayments: '{n} recurring payment | {n} recurring payments',
            loans: '{n} loan | {n} loans',
            copying: 'Copying…',
            copy: 'Copy history',
            copied: 'History copied ({n} transaction). Check the account balance and reconcile it if needed. | History copied ({n} transactions). Check the account balance and reconcile it if needed.',
            copyFailed: 'Couldn’t copy the history.',
        },
        push: {
            title: 'PAYMENT REMINDERS',
            intro: 'The day before it’s due, on the due date and once after, if the payment still isn’t ticked off. Payments from the same day come in a single notification.',
            noServer: 'The server doesn’t have push reminders enabled yet. You can still see payment alerts in the bell on the top bar.',
            iphone: 'On iPhone, reminders work once you add Rountine to your home screen (Share → Add to Home Screen) and open it from there.',
            unsupported: 'This browser doesn’t support push notifications.',
            turnOff: 'Turn off on this device',
            turnOn: 'Turn on on this device',
            onHere: 'on here',
            test: 'Send test',
            hour: 'At',
            dayBefore: 'also the day before',
            devices: 'Devices: {list}',
            device: 'device',
            enabled: 'Reminders turned on on this device.',
            disabled: 'Reminders turned off on this device.',
            blocked: 'Your browser is blocking notifications for Rountine — unblock them in the site settings and try again.',
            dismissed: 'Without the browser’s permission, reminders won’t arrive.',
            enableFailed: 'Couldn’t turn on reminders.',
            testSent: 'Sent to {n} device. | Sent to {n} devices.',
            testNone: 'No device received it — turn reminders on again.',
            testFailed: 'Couldn’t send the test.',
        },
        month: {
            title: 'FINANCIAL MONTH',
            startsOn: 'Starts on day',
            hint: 'If your salary arrives on e.g. the 10th, set 10 — budgets and “Left to spend” then run from payday to payday.',
        },
        remindersStep: {
            title: 'Turn on reminders',
            text: 'Tap “Turn on on this device” and allow notifications. They’re turned on separately on each phone and computer.',
        },
        dialog: {
            editAccount: 'Edit account',
            newAccount: 'New account',
            reconcile: 'Reconcile balance: {name}',
            editCategory: 'Edit category',
            newCategory: 'New category',
            removeCategory: 'Delete category “{name}”',
            removeAndMove: 'Delete and move',
        },
        flash: {
            hasTransactions: 'The account has transactions — archive it instead of deleting.',
            saveFailed: 'Couldn’t save.',
            saved: 'Saved.',
            accountSaved: 'Account saved.',
            accountDeleted: 'Account deleted.',
            categorySaved: 'Category saved.',
            categoryDeleted: 'Category “{name}” deleted.',
            monthSaved: 'Saved. “Left to spend” now counts from this day.',
            dayRange: 'The day must be between 1 and 31.',
        },
    },
    push: {
        browser: 'Browser',
    },
    quickAdd: {
        notFromGoal: 'Not from a goal',
        autoGoal: 'Automatic ({name})',
        auto: 'Automatic',
        noGoal: 'No goal',
        deposit: 'Deposit: {name}',
        withdrawal: 'Withdrawal: {name}',
        saveFailed: 'Couldn’t save.',
        onlyLastInstalment: 'Only the most recently paid loan instalment can be undone.',
        deleteFailed: 'Couldn’t delete.',
        dictateHint: 'Dictate — several transactions at once',
        dictateLabel: 'Dictate transactions',
        dictate: 'Dictate',
        receiptHint: 'Receipt — photo or PDF',
        receiptLabel: 'Add from a receipt',
        close: 'Close',
        toAccount: 'To account',
        needsTwoAccounts: 'An own transfer needs two accounts — add them in Wallet settings.',
        goal: 'Goal',
        split: 'Split into categories',
        other: 'Other…',
        pickCategory: 'Choose a category',
        noIncomeCategories: 'No income categories — add them in Wallet settings.',
        noExpenseCategories: 'No expense categories — add them in Wallet settings.',
        splitTitle: 'Split',
        splitOk: 'Adds up',
        splitMissing: '{amount} missing',
        splitTooMuch: '{amount} too much',
        partAmount: 'Amount of part {n}',
        removePart: 'Remove part {n}',
        addPart: '+ Part',
        fillRemainder: 'Rest to the last one',
        fromWhom: 'From',
        where: 'Where',
        optional: 'optional',
        fromWhomPlaceholder: 'e.g. employer',
        wherePlaceholder: 'e.g. Tesco',
        when: 'When',
        account: 'Account',
        fromGoal: 'From goal',
        fromGoalHint: 'An expense paid with the goal’s money will reduce the amount set aside.',
        addNote: '+ Note',
        note: 'Note',
        pays: 'Pays: {name} ({period})',
        instalmentOf: 'Instalment: {name}',
        overpaymentOf: 'Overpayment: {name}',
        delete: 'Delete',
        saving: 'Saving…',
        transferAmount: 'Transfer {amount}',
        addAmount: 'Add {amount}',
        transferNote: 'An own transfer changes the balances of {from} and {to}, but isn’t an expense or income.',
        loanUndo: 'The instalment or overpayment will be reversed on the loan.',
        cannotUndo: 'This can’t be undone.',
    },
    ai: {
        invalidInput: 'Invalid input.',
        noResponse: 'The AI service didn’t respond — try again.',
        notConfigured: 'AI isn’t configured on the server.',
        micDenied: 'The browser has no access to the microphone.',
        micFailed: 'Couldn’t turn on the microphone.',
        noSound: 'I can’t hear anything — check your microphone.',
        transcribing: 'Recognising speech…',
        noSpeech: 'No speech recognised — try again.',
        uploadFailed: 'Couldn’t upload the recording.',
        analyzing: 'Recognising transactions…',
        analyzeFailed: 'Couldn’t recognise the transactions.',
        readingMany: 'Reading {n} receipts…',
        readingOne: 'Reading the receipt…',
        receiptFailed: 'Couldn’t read the receipt.',
        noAmount: 'I couldn’t find any amount — say e.g. “pizza 15”.',
        itemError: 'Item {n}: {error}',
        reviewTitle: 'Check {n} transaction | Check {n} transactions',
        stopRecording: 'Stop recording',
        record: 'Record',
        listening: 'Listening… {s} s — tap when you’re done',
        voiceExample: 'e.g. “pizza 15 yesterday, fuel 60 by card, and 50 from Mum”',
        orType: 'or type',
        textPlaceholder: 'coffee 3, lunch 12 by card',
        analyze: 'Recognise',
        takePhoto: 'Take a photo or choose a file',
        fileTypes: 'photo, screenshot or PDF; up to 5 at once',
        extraNote: 'Note',
        extraNotePlaceholder: 'e.g. paid by card, dated 12/10',
        quote: '“{text}”',
        receiptWord: 'receipt',
        restore: 'Restore',
        toExpense: 'Income — change to expense',
        toIncome: 'Expense — change to income',
        amountN: 'Amount {n}',
        oneCategoryHint: 'Replace the split with a single category',
        oneCategory: 'one category',
        whereN: 'Where {n}',
        dateN: 'Date {n}',
        incomplete: 'Fill in the amount and category.',
        incompleteSplit: 'Fill in the amount and category (the split doesn’t match the amount).',
        back: 'Back',
        total: 'total {amount}',
        addAll: 'Add all',
    },
    attachments: {
        unsupported: 'This image format isn’t supported — take a screenshot or a JPEG photo.',
        addFailed: 'Couldn’t add the photo.',
        openFailed: 'Couldn’t open the file.',
        deleteFailed: 'Couldn’t delete the photo.',
        open: 'Open {name}',
        remove: 'Remove {name}',
        uploading: 'Uploading…',
        receiptPhoto: 'Receipt photo',
    },
    setup: {
        rent: 'Rent',
        electricity: 'Electricity',
        salaryName: 'Salary',
        saveFailed: 'Couldn’t save — try again.',
        step: 'STEP {n} OF {total}',
        balanceTitle: 'How much is in your account now?',
        balanceText: 'Wallet counts your balance from this amount. Later you can add more accounts — a card, cash, savings.',
        balanceLabel: 'Main account balance',
        historyTitle: 'You already have data in Finances',
        historyText: '{list}. You can copy them to Wallet once — Finances will stay unchanged, and from then on the two modules are separate.',
        copyAndStart: 'Copy history and start',
        startFresh: 'Start from scratch',
        next: 'Next',
        salaryTitle: 'When do you get paid?',
        salaryText: 'Before it arrives, Wallet will already count it in “Left to spend” — the start of the month won’t be in the red.',
        netAmount: 'Take-home pay',
        netAmountPlaceholder: 'e.g. 2500',
        day: 'Day',
        paymentsTitle: 'What do you pay every month?',
        paymentsText: 'Rent, utilities, subscriptions. Tick each one off with a single tap; until then it’s deducted from “Left to spend”. You can add loans in Plans.',
        paymentName: 'Payment name {n}',
        paymentAmount: 'Payment amount {n}',
        paymentDay: 'Payment day {n}',
        dayPlaceholder: 'day',
        addPayment: '+ Another payment',
        done: 'Done',
    },
    statement: {
        firstImport: '{name} (first import)',
        readingWithColumns: 'Reading with these columns…',
        readingFile: 'Reading the file…',
        columnsNotRecognised: 'Couldn’t recognise the columns automatically — mark them on the sample below.',
        noMovements: 'No transactions were read with these columns — correct them below.',
        noOperations: 'No transactions found in the file.',
        readFailed: 'Couldn’t read the file.',
        roles: {
            date: 'Date',
            amount: 'Amount (±)',
            debit: 'Debit',
            credit: 'Credit',
            counterparty: 'Payee',
            description: 'Description',
            balance: 'Balance after',
        },
        actions: {
            existing: 'Already there',
            plan: 'Pays a plan',
            loan: 'Loan instalment',
            transfer: 'Own transfer',
            new: 'New',
            skip: 'Skip',
        },
        newTransaction: 'New transaction',
        ownTransferEllipsis: 'Own transfer…',
        saveFailed: 'Couldn’t save — nothing was changed.',
        reconciling: 'Reconciling…',
        reconcileFailed: 'Couldn’t reconcile the balance.',
        mappingTitle: 'Which columns are which?',
        reviewTitle: 'Statement: {name}',
        doneTitle: 'Imported',
        intro: 'Download your account history from your bank as a CSV and choose it here. Nothing is saved until you check the preview; Wallet skips rows imported earlier on its own.',
        chooseFile: 'Choose a CSV file',
        banks: 'PKO, mBank, ING, Santander, Pekao, Millennium and others',
        row: 'row',
        headerRow: 'Header in row',
        dateFormat: 'Date format',
        decimalSeparator: 'Decimal separator',
        comma: 'comma',
        dot: 'full stop',
        negate: 'expenses are positive',
        readWithColumns: 'Read with these columns',
        alreadyImported: '{n} row was already imported | {n} rows were already imported',
        columns: 'columns',
        whichAccount: 'To which account',
        rulesNote: 'Categories you correct here will be remembered as rules for this payee.',
        otherFile: 'Another file',
        importButton: 'Import',
        result: {
            new: '{n} new transaction | {n} new transactions',
            existing: '{n} matched with existing entries | {n} matched with existing entries',
            paid: '{n} payment settled | {n} payments settled',
            transfer: '{n} own transfer | {n} own transfers',
            matches: 'The balance matches the bank: {amount} on {date}.',
            mismatch: 'The bank shows {bank} on {date}, Wallet {wallet} (difference {diff}).',
            reconcile: 'Reconcile balance with the bank',
            reconcileNote: 'The difference will be saved as “Untracked” — usually expenses from before the first import or an opening balance entered from memory.',
        },
    },
};
